// [S-534] Reusable "From Google Drive" import button. Mounted on the Import
// templates screen (target=templates) and the deals upload portal
// (target=deals). Same connect → pick → import experience; the server routes
// each selected file to the right pipeline by `target`.
//
// Flow: click → (if the caller hasn't linked their Drive yet) a Google consent
// popup opens inline → on completion the Picker opens → selected files import.
// The consent runs in a popup we open synchronously on click (opening it after
// an await trips the popup blocker); we then poll the mint endpoint until the
// server-side connection exists, and continue straight to the Picker. No detour
// to Settings — that dead-end was the old behaviour.
//
// Honest degrade (S-269 pattern): when the Google Picker API key isn't
// configured on this environment, the button is disabled with an explanation
// rather than failing on click.
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components";
import { getApiErrorMessage } from "../data/api";
import {
  useDriveImport,
  useMintDriveAccessToken,
  useStartDriveOAuth,
  type DriveImportSummary,
  type DriveImportTarget,
} from "../data/connectorsQuery";
import { drivePickerConfig, openDrivePicker } from "../lib/googlePicker";

// How often to re-check whether the inline OAuth consent has completed, and how
// long to keep waiting before giving up.
const CONNECT_POLL_MS = 1500;
const CONNECT_TIMEOUT_MS = 2 * 60 * 1000;

export function DriveImportButton({
  tenantId,
  target,
  onImported,
  appearance = "button",
}: {
  tenantId: string;
  target: DriveImportTarget;
  onImported?: (summary: DriveImportSummary) => void;
  /** "source" renders it as an upload-drop-zone source chip (Google "G" mark
   *  + "Google Drive") instead of the default standalone secondary button. */
  appearance?: "button" | "source";
}): JSX.Element {
  const isSource = appearance === "source";
  const sourceLabel = (
    <>
      <span className="upl__src__mark upl__src__mark--drive" aria-hidden="true">
        G
      </span>
      Google Drive
    </>
  );
  const config = drivePickerConfig();
  const mintToken = useMintDriveAccessToken();
  const startOAuth = useStartDriveOAuth();
  const driveImport = useDriveImport();
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [working, setWorking] = useState(false);

  if (config === null) {
    return (
      <div className="drvimport">
        <Button
          variant={isSource ? "bare" : "secondary"}
          className={isSource ? "upl__src upl__src--ghost" : undefined}
          disabled
          title="Google Drive import isn't set up on this environment yet (S-534)"
          data-testid="drvimport-button"
        >
          {isSource ? sourceLabel : "⬇ From Google Drive"}
        </Button>
        <span className="drvimport__note" data-testid="drvimport-not-configured">
          Not set up yet — a platform admin needs to add the Google Picker key.
        </span>
      </div>
    );
  }

  // Get a Picker access token. If the caller has already linked their Drive the
  // mint succeeds immediately (and the pre-opened popup is closed). Otherwise we
  // drive Google's consent screen inside `popup` and wait for the connection.
  const getAccessToken = async (popup: Window | null): Promise<string> => {
    try {
      const token = (await mintToken.mutateAsync({ tenantId })).accessToken;
      popup?.close(); // already connected — the pre-opened window isn't needed
      return token;
    } catch {
      // Not linked yet — run OAuth in the popup we opened on click.
      if (popup === null || popup.closed) {
        setPopupBlocked(true);
        throw new Error(
          "Your browser blocked the Google sign-in window. Allow pop-ups for this site and try again."
        );
      }
      const { authorizeUrl } = await startOAuth.mutateAsync({ tenantId });
      popup.location.href = authorizeUrl;
      return waitForDriveConnection(popup, () =>
        mintToken.mutateAsync({ tenantId }).then((r) => r.accessToken)
      );
    }
  };

  const run = async (popup: Window | null): Promise<void> => {
    setError(null);
    setStatus(null);
    setPopupBlocked(false);
    setWorking(true);
    try {
      const accessToken = await getAccessToken(popup);
      const picked = await openDrivePicker({
        apiKey: config.apiKey,
        oauthToken: accessToken,
        ...(config.appId !== undefined ? { appId: config.appId } : {}),
      });
      if (picked.length === 0) return; // user cancelled the picker

      const summary = await driveImport.mutateAsync({
        tenantId,
        target,
        fileIds: picked.map((p) => p.id),
      });
      const where = target === "deals" ? "corpus" : "library";
      setStatus(
        `Imported ${summary.imported} file${summary.imported === 1 ? "" : "s"}` +
          (summary.duplicates > 0
            ? ` (${summary.duplicates} already in your ${where})`
            : "") +
          "."
      );
      onImported?.(summary);
    } catch (e) {
      setError(
        (e instanceof Error && e.message.length > 0 ? e.message : null) ??
          getApiErrorMessage(e) ??
          "Google Drive import failed."
      );
    } finally {
      setWorking(false);
    }
  };

  const handleClick = (): void => {
    // Open the popup synchronously so the browser attributes it to this click —
    // deferring it past the mint await would trip the pop-up blocker. It shows a
    // placeholder until we know whether OAuth consent is actually needed.
    const popup = window.open(
      "about:blank",
      "csai-drive-connect",
      "width=520,height=680"
    );
    if (popup !== null) {
      try {
        popup.document.title = "Connecting to Google Drive…";
        popup.document.body.innerHTML =
          "<p style='font:14px system-ui,sans-serif;padding:24px'>Connecting to Google Drive…</p>";
      } catch {
        /* writing to about:blank can throw in some browsers — harmless */
      }
    }
    void run(popup);
  };

  return (
    <div className="drvimport">
      <Button
        variant={isSource ? "bare" : "secondary"}
        className={isSource ? "upl__src upl__src--ghost" : undefined}
        onClick={handleClick}
        loading={working}
        data-testid="drvimport-button"
      >
        {isSource ? sourceLabel : "⬇ From Google Drive"}
      </Button>
      {status !== null && (
        <span className="drvimport__note" role="status" data-testid="drvimport-status">
          {status}
        </span>
      )}
      {error !== null && (
        <span className="drvimport__err" role="alert" data-testid="drvimport-error">
          {error}
          {popupBlocked && (
            <>
              {" "}
              Or <Link to="/settings#connectors">connect in Settings</Link>.
            </>
          )}
        </span>
      )}
    </div>
  );
}

// Poll the mint callback until the inline OAuth consent has produced a
// server-side Drive connection (mint succeeds), the user closes the popup
// (cancel), or we time out. Resolves with the minted access token.
function waitForDriveConnection(
  popup: Window,
  mint: () => Promise<string>
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const startedAt = Date.now();
    let stopped = false;
    const settle = (fn: () => void): void => {
      if (stopped) return;
      stopped = true;
      fn();
    };
    const poll = async (): Promise<void> => {
      if (stopped) return;
      try {
        const token = await mint();
        settle(() => {
          popup.close();
          resolve(token);
        });
        return;
      } catch {
        /* not connected yet — keep waiting */
      }
      if (stopped) return;
      if (popup.closed) {
        settle(() => reject(new Error("Google Drive connection was cancelled.")));
        return;
      }
      if (Date.now() - startedAt > CONNECT_TIMEOUT_MS) {
        settle(() => {
          popup.close();
          reject(new Error("Timed out connecting to Google Drive."));
        });
        return;
      }
      setTimeout(() => void poll(), CONNECT_POLL_MS);
    };
    // First check immediately (in case the grant already went through), then on
    // an interval while the user works through the consent screen.
    void poll();
  });
}
